import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { makeStyles } from "@material-ui/core/styles"
import moment from "moment"
import { mobileGalleriesList } from "../v2_actions/gallery"
import { getLoggedInUserDisplayName } from "../v2_actions/auth"
import { getInitials } from "../v2_actions/photographer"
import WhiteLogo from "../assets/images/logo/white_logo.png"
import HomeIcon from "@material-ui/icons/Home"
import BarChartIcon from "@material-ui/icons/BarChart"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import MoreHorizIcon from "@material-ui/icons/MoreHoriz"
import { MobileGalleryDetailModal } from "./MobileGalleryDetailModal"

const PAGE_SIZE = 20

export const MobileGalleries = () => {
    const [galleries, setGalleries] = useState([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const displayName = getLoggedInUserDisplayName()
    const initials = getInitials(displayName)

    const fetchPage = (pageNum) => {
        setLoading(true)
        dispatch(mobileGalleriesList.updateListFilter({ page: pageNum, items_per_page: PAGE_SIZE }))
        dispatch(mobileGalleriesList.fetchListIfNeeded(true)).then((results) => {
            if (results && results.length > 0) {
                setGalleries((prev) => pageNum === 1 ? results : [...prev, ...results])
                setHasMore(results.length === PAGE_SIZE)
            } else {
                setHasMore(false)
            }
            setLoading(false)
        })
    }

    useEffect(() => {
        fetchPage(1)
    }, [])

    const handleLoadMore = () => {
        const nextPage = page + 1
        setPage(nextPage)
        fetchPage(nextPage)
    }

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <img src={WhiteLogo} alt="bossfotos" className={classes.logo} />
                <div className={classes.avatar}>{initials}</div>
            </div>

            <MobileGalleryDetailModal>
                {({ onGalleryClick }) => (
                    <div className={classes.content}>
                        <div className={classes.pageTitle}>Galleries</div>
                        <div className={classes.galleriesList}>
                            {galleries.map((gallery) => (
                                <div key={gallery.id} className={classes.galleryRow} onClick={() => onGalleryClick(gallery.id)}>
                                    <div className={classes.galleryName}>{gallery.session_name}</div>
                                    <div className={classes.galleryMeta}>{gallery.number_of_photos_in_gallery} photos</div>
                                    <div className={classes.galleryDate}>{moment(gallery.shot_at_datetime).format("D MMM YYYY")}</div>
                                </div>
                            ))}
                        </div>
                        {hasMore && !loading && (
                            <div className={classes.loadMore} onClick={handleLoadMore}>
                                Load More
                            </div>
                        )}
                    </div>
                )}
            </MobileGalleryDetailModal>

            <div className={classes.bottomNav}>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile")}>
                    <HomeIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>Home</span>
                </div>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile-sales")}>
                    <BarChartIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>Sales</span>
                </div>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile-upload")}>
                    <CloudUploadIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>Upload</span>
                </div>
                <div className={classes.navItem}>
                    <MoreHorizIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>More</span>
                </div>
            </div>
        </div>
    )
}

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        fontFamily: "Helvetica, Raleway, sans-serif",
        paddingBottom: "60px",
    },
    header: {
        background: "linear-gradient(135deg, #0d2137 0%, #1e88e5 100%)",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        height: "36px",
        width: "auto",
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: "#1565c0",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        fontWeight: 600,
        border: "2px solid #fff",
    },
    content: {
        padding: "16px",
        flex: 1,
    },
    pageTitle: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#42a5f5",
        textTransform: "uppercase",
        fontFamily: "Raleway, sans-serif",
        letterSpacing: "0.5px",
        marginBottom: "12px",
    },
    galleriesList: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },
    galleryRow: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "14px 16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    galleryName: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#333",
        marginBottom: "2px",
        lineHeight: 1.3,
    },
    galleryMeta: {
        fontSize: "12px",
        color: "#666",
        marginBottom: "1px",
        lineHeight: 1.3,
    },
    galleryDate: {
        fontSize: "12px",
        color: "#666",
        lineHeight: 1.3,
    },
    loadMore: {
        textAlign: "center",
        padding: "14px",
        marginTop: "8px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        color: "#1e88e5",
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
    },
    bottomNav: {
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        padding: "8px 0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
    },
    navItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        padding: "4px 12px",
    },
    navIcon: {
        fontSize: "24px",
        color: "#0d2137",
        marginBottom: "2px",
    },
    navLabel: {
        fontSize: "10px",
        color: "#0d2137",
    },
})
